import { useState, useEffect, useCallback, useRef } from 'react';
import { api, extractErrorMessage } from '../../api/client';
import { useXlsxExport } from '../../hooks/useXlsxExport';
import { useDebounce } from '../../hooks/useDebounce';
import { useToast } from '../../components/Toast';
import { CodigoArticulo } from '../../components/CodigoArticulo';
import type { CatItem, CostosSku, FormacionPrecio, Params } from './types';
import { costoConIvaSegun, simularPrecios, TIPOS_PUBLICACION, type ItemSimulacion } from './simular';
import { fmt } from './formato';
import { AYUDA_SEMAFORO, SemaforoRentabilidad } from './ui';

// ─── Tabla de publicaciones ────────────────────────────────────────────────
// La cuenta la hace el backend con la misma fórmula y los parámetros guardados que «Por publicación».
const PAGE_SIZE = 10;

const redondear2 = (n: number) => Math.round(n * 100) / 100;

/** Ítem a calcular de un SKU del catálogo; null sin costo. */
function itemDe(item: CatItem, p: Params): ItemSimulacion | null {
  // El catálogo manda los decimales como texto ("0.4000"): se pasan a número antes de enviarlos.
  const costo = Number(item.costoPromedio ?? 0);
  if (!(costo > 0)) return null;
  const peso = Number(item.pesoKg ?? 0);
  const precio = Number(item.precioVenta ?? 0);
  return {
    ...(item.mlaId ? { mlaId: item.mlaId } : {}),
    costoConIva: redondear2(costoConIvaSegun(costo, p)),
    pesoKg: peso > 0 ? peso : null,
    tipo: p.tipo,
    rentabilidadPct: p.rentabilidadPct,
    precioActual: precio > 0 ? precio : null,
  };
}

/** Tope de SKU por GET precios/costos (backend: MAX_SKUS_COSTOS). */
const MAX_SKUS_COSTOS = 200;

/**
 * Un pack o combo no tiene costo promedio propio en el catálogo: se completa con el de su BOM (costo promedio de los SKU
 * base × cantidad), el mismo cálculo del backend que «Costos (vista previa)». Si no se puede calcular (o el pedido
 * falla), queda sin costo: el resto de la tabla se muestra igual.
 */
async function conCostoDePacks(items: CatItem[]): Promise<CatItem[]> {
  const sinCosto = [...new Set(items.filter(i => !(Number(i.costoPromedio ?? 0) > 0)).map(i => i.skuInterno))];
  const costoBom = new Map<string, number>();
  try {
    for (let i = 0; i < sinCosto.length; i += MAX_SKUS_COSTOS) {
      const lote = sinCosto.slice(i, i + MAX_SKUS_COSTOS);
      const costos = await api.get<CostosSku[]>(`/api/v3/precios/costos?skus=${lote.map(encodeURIComponent).join(',')}`);
      for (const c of costos) {
        const v = c.esCombo ? c.costos.costoPromedio.sinIva : null;
        if (v != null && v > 0) costoBom.set(c.sku, v);
      }
    }
  } catch {
    // Sin costo del BOM los packs quedan "sin costo", como antes; no tiramos la página entera.
  }
  return items.map(i => (costoBom.has(i.skuInterno) ? { ...i, costoPromedio: costoBom.get(i.skuInterno) } : i));
}

/** Calcula los SKU con costo; deja null en los que no tienen. */
async function calcular(items: CatItem[], p: Params): Promise<Array<FormacionPrecio | null>> {
  const pedidos = items.map(i => itemDe(i, p));
  const resultados = await simularPrecios(pedidos.filter((x): x is ItemSimulacion => x !== null));
  let k = 0;
  return pedidos.map(x => (x ? resultados[k++] ?? null : null));
}

export function TablaPublicaciones({ p }: { p: Params }) {
  const [page, setPage]         = useState(0);
  const [total, setTotal]       = useState(0);
  const [rows, setRows]         = useState<CatItem[]>([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [q, setQ]               = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const { exportXlsx }          = useXlsxExport();
  const debounceRef             = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [calculos, setCalculos] = useState<Array<FormacionPrecio | null>>([]);
  const toast = useToast();
  // Como en la Calculadora: se recalcula cuando los parámetros dejan de cambiar.
  const pDebounced = useDebounce(p, 300);

  useEffect(() => {
    let cancelado = false;
    setCalculos([]);
    if (rows.length === 0) return;
    calcular(rows, pDebounced)
      .then(r => { if (!cancelado) { setCalculos(r); setError(null); } })
      .catch(e => { if (!cancelado) setError(extractErrorMessage(e)); });
    return () => { cancelado = true; };
  }, [rows, pDebounced]);

  const fetchPage = useCallback(async (pageNum: number, search: string) => {
    setLoading(true);
    setError(null);
    try {
      const offset = pageNum * PAGE_SIZE;
      const res = await api.getWithResponse<CatItem[]>(
        `/api/v3/catalogo?limit=${PAGE_SIZE}&offset=${offset}${search ? `&q=${encodeURIComponent(search)}` : ''}`
      );
      setRows(await conCostoDePacks(res.data));
      const totalHeader = res.headers.get('X-Total-Count');
      setTotal(totalHeader ? parseInt(totalHeader) : 0);
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPage(page, q); }, [page, fetchPage]);

  const handleSearch = (val: string) => {
    setQ(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(0); fetchPage(0, val); }, 350);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Descarga XLS — trae TODOS los registros (hasta 5000) para el export
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const tipo = TIPOS_PUBLICACION.find(t => t.id === p.tipo)?.label ?? p.tipo;
      // Traemos en lotes de 200
      const all: CatItem[] = [];
      let off = 0;
      while (true) {
        const res = await api.getWithResponse<CatItem[]>(
          `/api/v3/catalogo?limit=200&offset=${off}${q ? `&q=${encodeURIComponent(q)}` : ''}`
        );
        all.push(...res.data);
        const tot = parseInt(res.headers.get('X-Total-Count') ?? '0');
        if (all.length >= tot || res.data.length === 0) break;
        off += 200;
      }

      const conPacks = await conCostoDePacks(all);
      const calculosTodos = await calcular(conPacks, p);
      const exportRows = conPacks.map((item, i) => {
        const calc = calculosTodos[i];
        return {
          sku:        item.codigo || item.skuInterno,
          proveedorArticulo: item.proveedorIdentidad?.nombre ?? '',
          titulo:     item.titulo ?? '',
          marca:      item.marca ?? '',
          proveedor:  item.proveedorEstandar?.nombre ?? '',
          costo:      item.costoPromedio ?? 0,
          pesoKg:     item.pesoKg ?? 0,
          envio:      calc?.envio.costo ?? 0,
          precioSugerido: calc?.precioSugeridoRedondeado ?? 0,
          rentNetaPct: calc?.rentabilidadNeta?.pctSobreCosto ?? 0,
          precioActualML: item.precioVenta ?? 0,
          rentNetaHoyPct: calc?.rentabilidadNetaActual?.pctSobreCosto ?? '',
          mlaId:      item.mlaId ?? '',
          publicacion: tipo,
        };
      });

      await exportXlsx(
        [
          { key: 'sku',           header: 'SKU' },
          { key: 'proveedorArticulo', header: 'Proveedor del artículo' },
          { key: 'titulo',        header: 'Título' },
          { key: 'marca',         header: 'Marca' },
          { key: 'proveedor',     header: 'Proveedor' },
          { key: 'costo',         header: 'Costo promedio' },
          { key: 'pesoKg',        header: 'Peso (kg)' },
          { key: 'envio',         header: 'Envío' },
          { key: 'precioSugerido',header: 'Precio sugerido' },
          { key: 'rentNetaPct',   header: 'Rent. neta % del costo' },
          { key: 'precioActualML',header: 'Precio actual ML' },
          { key: 'rentNetaHoyPct',header: 'Rent. neta hoy % del costo' },
          { key: 'mlaId',         header: 'MLA ID' },
          { key: 'publicacion',   header: 'Tipo publicación' },
        ],
        exportRows,
        'formacion-precios',
      );
    } catch (e) {
      toast.addToast(`No se pudo descargar el XLS: ${extractErrorMessage(e)}`, 'error');
    } finally {
      setExportLoading(false);
    }
  };

  const tipo = TIPOS_PUBLICACION.find(t => t.id === p.tipo)?.label ?? p.tipo;

  return (
    <div className="space-y-4">
      {/* Barra de búsqueda + export */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input type="text" value={q} onChange={e => handleSearch(e.target.value)}
            placeholder="Buscar por SKU, título o marca..."
            className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-200" />
        </div>
        <button onClick={handleExport} disabled={exportLoading || loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          {exportLoading ? 'Exportando...' : 'Descargar XLS'}
        </button>
        <span className="text-xs text-slate-400 ml-1">{total} registros</span>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">SKU</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Título</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Marca</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Costo</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">Envío</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">Precio sugerido</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">Precio actual</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap"
                  title={`Con el precio actual. ${AYUDA_SEMAFORO} No descuenta el impuesto a las Ganancias.`}>Rent. neta hoy</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="py-12 text-center text-slate-400 text-sm">Cargando...</td></tr>
              )}
              {!loading && error && (
                <tr><td colSpan={8} className="py-12 text-center text-red-500 text-sm">{error}</td></tr>
              )}
              {!loading && !error && rows.length === 0 && (
                <tr><td colSpan={8} className="py-12 text-center text-slate-400 text-sm">Sin resultados</td></tr>
              )}
              {!loading && rows.map((item, i) => {
                const calc = calculos[i] ?? null;
                return (
                  <tr key={item.skuInterno} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/40'}`}>
                    <td className="py-3 px-4 text-xs text-slate-600 whitespace-nowrap"><CodigoArticulo sku={item.skuInterno} /></td>
                    <td className="py-3 px-4 text-slate-700 max-w-[220px] truncate" title={item.titulo}>{item.titulo ?? '—'}</td>
                    <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">{item.marca ?? '—'}</td>
                    <td className="py-3 px-4 text-right font-mono text-slate-700 whitespace-nowrap">{fmt(item.costoPromedio)}</td>
                    <td className="py-3 px-4 text-right font-mono text-slate-500 text-xs whitespace-nowrap">
                      {calc ? fmt(calc.envio.costo) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-3 px-4 text-right font-mono font-semibold text-brand-700 whitespace-nowrap">
                      {calc?.precioSugeridoRedondeado != null ? fmt(calc.precioSugeridoRedondeado) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-slate-600 whitespace-nowrap">
                      {item.precioVenta ? fmt(item.precioVenta) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      {calc?.rentabilidadNetaActual
                        ? <SemaforoRentabilidad netaPct={calc.rentabilidadNetaActual.pctSobreCosto} objetivoPct={pDebounced.rentabilidadPct} />
                        : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <span className="text-xs text-slate-400">
              Página {page + 1} de {totalPages} · {total} registros
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(0)} disabled={page === 0}
                className="p-1.5 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30 transition-colors" title="Primera">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7M18 19l-7-7 7-7"/></svg>
              </button>
              <button onClick={() => setPage(p2 => Math.max(0, p2 - 1))} disabled={page === 0}
                className="p-1.5 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30 transition-colors" title="Anterior">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>
              </button>
              {/* Página actual y vecinas */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const start = Math.max(0, Math.min(page - 2, totalPages - 5));
                const pg = start + i;
                return (
                  <button key={pg} onClick={() => setPage(pg)}
                    className={`w-7 h-7 rounded text-xs font-medium transition-colors ${pg === page ? 'bg-brand-500 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                    {pg + 1}
                  </button>
                );
              })}
              <button onClick={() => setPage(p2 => Math.min(totalPages - 1, p2 + 1))} disabled={page >= totalPages - 1}
                className="p-1.5 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30 transition-colors" title="Siguiente">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
              </button>
              <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}
                className="p-1.5 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30 transition-colors" title="Última">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M6 5l7 7-7 7"/></svg>
              </button>
            </div>
          </div>
        )}
      </div>

      <p className="text-[11px] text-slate-400">
        Misma cuenta que «Por publicación»: comisión {tipo} de la tabla de Parámetros, rentabilidad {p.rentabilidadPct}% del costo, envío según el peso del catálogo.
        {p.costoConIva ? ' Costo tratado como con IVA.' : ` IVA proveedor ${p.ivaAlicuota}% agregado al costo.`}
      </p>
    </div>
  );
}
